import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { site } from "@/lib/siteContent";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.15 + i * 0.12,
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen bg-navy-dark text-white overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={site.hero.imageUrl}
          alt="Modern corporate skyline"
          className="w-full h-full object-cover opacity-50 scale-105"
        />
        {/* Gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-tr from-navy-dark via-navy-dark/85 to-navy-dark/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-transparent to-navy-dark/70" />
        {/* Decorative gold lines */}
        <div className="absolute inset-x-0 top-20 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      </div>

      {/* Decorative diagonal bar accent */}
      <div className="absolute right-0 top-0 h-full w-1/3 hidden lg:block pointer-events-none">
        <div className="absolute top-1/4 right-12 w-40 h-40 border border-gold/30 rotate-45" />
        <div className="absolute bottom-1/4 right-32 w-24 h-24 border border-gold/20 rotate-12" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 min-h-screen flex flex-col justify-end pb-24 pt-40">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <motion.div
              custom={0}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-12 bg-gold" />
              <span className="text-[11px] font-bold uppercase tracking-[0.32em] text-gold">
                {site.hero.overline}
              </span>
            </motion.div>

            <motion.h1
              custom={1}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              data-testid="hero-heading"
              className="font-display font-medium text-white text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight text-balance"
            >
              {site.hero.headingLine1}
              <br />
              <span className="italic text-gold">{site.hero.headingLine2}</span>
            </motion.h1>

            <motion.p
              custom={2}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mt-10 max-w-xl text-base lg:text-lg text-white/75 leading-relaxed"
            >
              {site.hero.subheading}
            </motion.p>

            <motion.div
              custom={3}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mt-12 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy px-7 py-4 text-xs font-bold uppercase tracking-[0.22em] transition-all duration-300 hover:translate-y-[-2px]"
              >
                {site.hero.ctaPrimary}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <a
                href="#businesses"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold px-7 py-4 text-xs font-bold uppercase tracking-[0.22em] transition-all duration-300 hover:bg-white/5"
              >
                {site.hero.ctaSecondary}
              </a>
            </motion.div>
          </div>

          {/* Right side stat / tagline pillar */}
          <motion.div
            custom={4}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-white/10"
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold mb-4">
              Our Promise
            </div>
            <p className="font-display text-2xl lg:text-3xl text-white/95 leading-snug">
              Building Businesses.<br />
              Empowering People.<br />
              <span className="text-gold italic">Creating Legacy.</span>
            </p>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.a
          href="#businesses"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 left-6 md:left-10 lg:left-16 flex items-center gap-3 text-white/50 hover:text-gold text-[11px] uppercase tracking-[0.28em] font-medium"
        >
          <span className="inline-block w-10 h-px bg-current" />
          Scroll
          <ArrowDown size={14} className="animate-bounce" />
        </motion.a>
      </div>
    </section>
  );
}
